import { useMemo } from 'react';
import { extractHeadingTagNumber, makeHeadingElementKey } from '../../../utils/heading-structure-utils';
import { ElementStatus } from '../../rule-element/types';
import { HeadingStructureFailedReason } from './types';
const buildProposedTagMap = aiResult => {
  var _aiResult$suggestions;
  const map = new Map();
  aiResult == null || (_aiResult$suggestions = aiResult.suggestions) == null || _aiResult$suggestions.forEach(suggestion => {
    if (!suggestion.proposedTag) {
      return;
    }
    map.set(makeHeadingElementKey(suggestion.id, suggestion.attributeId, suggestion.uiElementId), suggestion.proposedTag);
  });
  return map;
};

/**
 * Given a list of headings, the current AI suggestion result, and an injected
 * `ruleChecker`, returns a map of elementId → previewed `ElementStatus`
 * representing what each heading's status would be if all AI suggestions
 * were applied.
 *
 * Returns an empty map when no checker is provided or no suggestion exists —
 * callers should fall back to `heading.status` in that case.
 */
export const useHeadingStatusPreview = (headings, aiResult, ruleChecker) => {
  return useMemo(() => {
    const empty = new Map();
    if (!ruleChecker) {
      return empty;
    }
    const proposedTagByKey = buildProposedTagMap(aiResult);
    if (proposedTagByKey.size === 0) {
      return empty;
    }
    const headingKeys = headings.map(heading => makeHeadingElementKey(heading.componentId, heading.attributeId, heading.elementId));
    const previewHtmlStrings = headings.map((heading, index) => {
      const proposedTag = proposedTagByKey.get(headingKeys[index]);
      const htmlString = proposedTag ? `<${proposedTag.toLowerCase()}></${proposedTag.toLowerCase()}>` : heading.htmlString ?? '';
      return {
        htmlString,
        id: heading.elementId
      };
    });
    const {
      failedHeadings,
      failedReasons
    } = ruleChecker({
      htmlStrings: previewHtmlStrings
    });
    const failedIds = new Set(failedHeadings.map(h => h.id));
    const isNoMainHeading = failedReasons.includes(HeadingStructureFailedReason.NoMainHeading);
    const isMultipleMainHeading = failedReasons.includes(HeadingStructureFailedReason.MultipleMainHeadings);
    const result = new Map();
    headings.forEach((heading, index) => {
      const proposedTag = proposedTagByKey.get(headingKeys[index]);
      const previewTagSource = proposedTag ?? heading.htmlString ?? heading.headingDefinition;
      const previewTagNumber = extractHeadingTagNumber(previewTagSource);
      const passed = !(failedIds.has(heading.elementId) || isNoMainHeading && index === 0 || isMultipleMainHeading && previewTagNumber === 1);
      result.set(heading.elementId, passed ? ElementStatus.Passed : ElementStatus.Failed);
    });
    return result;
  }, [headings, aiResult, ruleChecker]);
};
//# sourceMappingURL=use-heading-status-preview.js.map